import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "components/ui/card";
import { message, Modal } from "antd";
import { toast } from "components/ui/toast";
import { revokeOtherSessions } from "Explore/Services/Settings";
import {
  ShieldCheck,
  Laptop,
  Check,
  Copy,
  LogOut,
  Sparkles,
  Lock,
} from "lucide-react";

const AccountSection = () => {
  const user = useSelector((state) => state.user?.data) || {};
  const [copiedId, setCopiedId] = useState(false);
  const [revoking, setRevoking] = useState(false);

  const userId = user.id || user._id || user.username || "usr_academic_01";
  const userRole = user.role || "subscribed";

  const handleCopyId = () => {
    try {
      if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(String(userId));
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = String(userId);
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
    } catch (e) {
      console.warn("Clipboard write failed:", e);
    }
    setCopiedId(true);
    toast.success("User ID copied to clipboard.", { title: "Copied" });
    try { message.success("User ID copied to clipboard."); } catch (e) {}
    setTimeout(() => setCopiedId(false), 2000);
  };

  const handleRevokeSessions = () => {
    Modal.confirm({
      title: "Sign out of all other sessions?",
      content:
        "This will invalidate all access tokens on other browsers or devices. You will stay signed in on this browser.",
      okText: "Sign Out Other Sessions",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        setRevoking(true);
        try {
          const res = await revokeOtherSessions();
          if (res && res.status === "success") {
            const count =
              res.revoked_count !== undefined
                ? ` (${res.revoked_count} session${res.revoked_count === 1 ? "" : "s"} revoked)`
                : "";
            const successMsg = `${res.message || "All other active sessions have been signed out."}${count}`;
            toast.success(successMsg, { title: "Sessions Revoked" });
            try { message.success(successMsg); } catch (e) {}
          } else {
            throw new Error(res?.message || "Failed to sign out other sessions.");
          }
        } catch (err) {
          console.error("Revoke sessions error:", err);
          const detail =
            err?.response?.data?.detail ||
            err?.response?.data?.message ||
            "Failed to sign out other sessions. Please try again.";
          toast.error(detail, { title: "Action Failed" });
          try { message.error(detail); } catch (e) {}
        } finally {
          setRevoking(false);
        }
      },
    });
  };

  // Browser info
  const browserName = navigator.userAgent.includes("Chrome")
    ? "Chrome / Chromium"
    : navigator.userAgent.includes("Firefox")
    ? "Mozilla Firefox"
    : navigator.userAgent.includes("Safari")
    ? "Apple Safari"
    : "Modern Browser";

  return (
    <div className="max-w-4xl space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 tracking-tight">Account & Security</h2>
        <p className="text-sm text-gray-500 mt-1">
          Review your subscription tier, authentication credentials, and session management.
        </p>
      </div>

      {/* Subscription & Tier Info */}
      <Card className="border-gray-200 shadow-2xs">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base font-semibold text-gray-900">Subscription & Role</CardTitle>
              <CardDescription className="text-xs text-gray-500">
                Your current authorization tier for Academic Online research features.
              </CardDescription>
            </div>
            <Badge className="bg-indigo-600 hover:bg-indigo-700 text-white gap-1 px-2.5 py-1 text-xs">
              <Sparkles size={12} />
              {userRole.toUpperCase()}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 rounded-lg bg-gray-50/70 border border-gray-100">
            <div>
              <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider block">
                Account ID
              </span>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-xs font-mono font-medium text-gray-800 truncate max-w-[140px]">
                  {userId}
                </span>
                <button
                  type="button"
                  onClick={handleCopyId}
                  className="text-gray-400 hover:text-gray-700 transition-colors"
                  title="Copy ID"
                >
                  {copiedId ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
                </button>
              </div>
            </div>

            <div>
              <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider block">
                Plan Access
              </span>
              <span className="text-xs font-semibold text-gray-900 mt-0.5 block">
                Full Enterprise Analytics
              </span>
            </div>

            <div>
              <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider block">
                Institution Seat
              </span>
              <span className="text-xs font-medium text-gray-800 mt-0.5 block truncate">
                {user.admin_institution_name || user.institution || "Institutional License"}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Authentication Notice */}
      <Card className="border-gray-200 shadow-2xs">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-gray-100 flex items-center justify-center text-gray-700">
              <Lock size={15} />
            </div>
            <div>
              <CardTitle className="text-base font-semibold text-gray-900">
                Authentication & Identity
              </CardTitle>
              <CardDescription className="text-xs text-gray-500">
                Single Sign-On (SSO) and account security credentials.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="p-3.5 rounded-lg bg-gray-50/70 border border-gray-100 flex items-start justify-between">
            <div>
              <span className="text-xs font-semibold text-gray-800 block">
                Single Sign-On (SSO) Managed
              </span>
              <p className="text-xs text-gray-500 mt-0.5 max-w-lg">
                Your credentials and authentication security are centrally managed by your
                institutional identity provider. Contact your organization administrator to update login
                credentials.
              </p>
            </div>
            <Badge variant="secondary" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
              SSO Active
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Active Sessions & Security */}
      <Card className="border-gray-200 shadow-2xs">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <ShieldCheck size={16} />
            </div>
            <div>
              <CardTitle className="text-base font-semibold text-gray-900">
                Security & Active Sessions
              </CardTitle>
              <CardDescription className="text-xs text-gray-500">
                Devices and browsers currently authenticated with your account.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3.5 rounded-lg border border-gray-200 bg-white">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600">
                <Laptop size={18} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-900">{browserName}</span>
                  <Badge variant="secondary" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">
                    Current Session
                  </Badge>
                </div>
                <span className="text-[11px] text-gray-400">Authenticated via Access Token • Active now</span>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-1">
            <p className="text-xs text-gray-500">
              Suspicious activity? Terminate all other browser sessions immediately.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRevokeSessions}
              disabled={revoking}
              className="gap-1.5 text-xs h-8 text-gray-700 hover:bg-gray-100"
            >
              <LogOut size={13} />
              {revoking ? "Signing Out..." : "Sign Out Other Sessions"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountSection;
